
import { supabase } from "@/integrations/supabase/client";
import { Movie } from "@/types/movie";
import { MovieNotFoundError, isKnownFailedMovie, addFailedMovieId } from "@/utils/movieRequestCache";
import { normalizeImagePaths } from "@/utils/imageUtils";

// Interface for the universal movie details response - Pure camelCase
export interface UniversalMovieDetails {
  tmdbId: number;
  title: string;
  director: string;
  year?: number;
  releaseDate?: string;
  runtime?: number;
  genres?: { id: number; name: string }[];
  posterPath?: string | null;
  backdropPath?: string | null;
  overview?: string | null;
  voteAverage?: number;
  popularity?: number;
  tags?: string[];
  favorite?: boolean;
  planToWatch?: boolean;
}

/**
 * PHASE 3: Universal function with NO fallback genres - only real TMDB metadata
 * Enhanced with better error handling and strict metadata requirements
 */
export async function getMovieEnriched(
  tmdbId: number,
  uid?: string,
  signal?: AbortSignal
): Promise<Movie> {
  console.log(`[UniversalMovieService] PHASE 3: Fetching complete metadata for tmdbId: ${tmdbId}`);
  
  // Check if this ID is known to fail
  if (isKnownFailedMovie(tmdbId)) {
    console.warn(`[UniversalMovieService] Skipping known failed movie ID: ${tmdbId}`);
    throw new MovieNotFoundError(tmdbId);
  }
  
  const options: any = {
    body: { 
      tmdbId: tmdbId, 
      uid 
    }
  };
  
  if (signal) {
    options.signal = signal;
  }
  
  let retries = 2;
  
  while (retries >= 0) {
    try {
      const TIMEOUT_MS = 10000;
      
      const result = await Promise.race([
        supabase.functions.invoke('get_movie_details', options),
        createTimeout(TIMEOUT_MS)
      ]);
      
      const { data, error } = result as { data: any, error: any };
      
      if (error) {
        console.error('[UniversalMovieService] Error:', error);
        
        if (error.message && (error.message.includes('404') || error.status === 404)) {
          addFailedMovieId(tmdbId);
          throw new MovieNotFoundError(tmdbId);
        }
        
        if (retries > 0) {
          retries--;
          console.log(`[UniversalMovieService] Retrying (${retries} attempts left)...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        
        throw error;
      }
      
      if (data && data.error) {
        console.error('[UniversalMovieService] Edge function error:', data.error);
        
        if (data.status === 404 || data.error.includes('404')) {
          addFailedMovieId(tmdbId);
          throw new MovieNotFoundError(tmdbId);
        }
        
        if (retries > 0) {
          retries--;
          console.log(`[UniversalMovieService] Retrying after edge function error (${retries} attempts left)...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        
        throw new Error(data.message || data.error);
      }
      
      console.log(`[UniversalMovieService] PHASE 3: Successfully enriched: ${data.title}`);
      return normalizeUniversalMovieDetails(data as UniversalMovieDetails);
      
    } catch (error: any) {
      if (error.message?.includes('timed out')) {
        if (retries > 0) {
          retries--;
          console.log(`[UniversalMovieService] Timeout occurred, retrying (${retries} attempts left)...`);
          await new Promise(resolve => setTimeout(resolve, 1500));
          continue;
        }
        console.error(`[UniversalMovieService] Final timeout for movie ID ${tmdbId}`);
        throw new Error(`Request timed out after multiple attempts. Please try again.`);
      }
      
      if (error.status === 404 || (error.data && error.data.status === 404)) {
        addFailedMovieId(tmdbId);
        throw new MovieNotFoundError(tmdbId);
      }
      
      if (retries > 0) {
        retries--;
        console.log(`[UniversalMovieService] Error occurred, retrying (${retries} attempts left):`, error.message);
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }
      
      throw error;
    }
  }
  
  throw new Error('Failed to fetch movie after all retry attempts');
}

/**
 * PHASE 3: Strict normalization - NO fallback genres, only real TMDB data
 */
function normalizeUniversalMovieDetails(details: UniversalMovieDetails): Movie {
  // PHASE 3: Get real genre/tag information - NO fallbacks
  let genreNames = details.genres?.map(genre => genre.name) || details.tags || [];
  
  // PHASE 3: STRICT - Do not provide fallback genres
  if (genreNames.length === 0) {
    console.warn(`[UniversalMovieService] PHASE 3: Movie ${details.title} has no genre data - this should not happen with the new pipeline`);
    // Throw error instead of using fallback
    throw new Error(`Movie ${details.title} (${details.tmdbId}) has no genre metadata available`);
  }
  
  const normalized = {
    tmdbId: details.tmdbId,
    title: details.title || 'Unknown Title',
    director: details.director || 'Unknown Director',
    year: details.year || (details.releaseDate ? new Date(details.releaseDate).getFullYear() : 0),
    tags: genreNames, // Real genre data only
    posterPath: details.posterPath,
    backdropPath: details.backdropPath,
    overview: details.overview || '',
    favorite: details.favorite || false,
    planToWatch: details.planToWatch || false,
    genres: genreNames,
    releaseDate: details.releaseDate,
    runtime: details.runtime,
    voteAverage: details.voteAverage,
    popularity: details.popularity || 0,
    source: "movie" as const
  };
  
  console.log(`[UniversalMovieService] PHASE 3: Normalized ${normalized.title} with ${genreNames.length} real genres: ${genreNames.join(', ')}`);
  
  return normalizeImagePaths(normalized);
}

/**
 * Creates a promise that will reject after the specified timeout
 */
function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms);
  });
}
